import { DEFAULT_SETTINGS, HOME_PITCH, HOME_POSITION, type ExperienceSettings, type PlayerSnapshot, type WorldSave } from "../core/types";

const SAVE_PREFIX = "countryside-walk:v1:";
const ACTIVE_SEED_KEY = "countryside-walk:active-seed";

export function normalizeSeed(value: string): string {
  const normalized = value.trim().replace(/[^\p{L}\p{N}_-]+/gu, "-").replace(/^-+|-+$/g, "");
  return normalized.slice(0, 48) || "misty-rice-fields";
}

export function getUrlSeed(search = window.location.search): string | null {
  const value = new URLSearchParams(search).get("seed");
  return value ? normalizeSeed(value) : null;
}

export function createDefaultSave(seed: string): WorldSave {
  return {
    version: 2,
    seed: normalizeSeed(seed),
    updatedAt: Date.now(),
    player: {
      position: [...HOME_POSITION],
      yaw: 0,
      pitch: HOME_PITCH,
    },
    settings: structuredClone(DEFAULT_SETTINGS),
  };
}

export function loadWorldSave(seed: string, storage?: Storage): WorldSave {
  const normalized = normalizeSeed(seed);
  const raw = safeGet(resolveStorage(storage), `${SAVE_PREFIX}${normalized}`);
  if (!raw) return createDefaultSave(normalized);
  try {
    const parsed = JSON.parse(raw) as Partial<Omit<WorldSave, "version">> & { version?: number };
    if ((parsed.version !== 1 && parsed.version !== 2) || parsed.seed !== normalized || !parsed.player || !parsed.settings) {
      return createDefaultSave(normalized);
    }
    return {
      version: 2,
      seed: normalized,
      updatedAt: typeof parsed.updatedAt === "number" ? parsed.updatedAt : Date.now(),
      player: parsed.version === 1 ? createHomePlayer() : sanitizePlayer(parsed.player),
      settings: sanitizeSettings(parsed.settings),
    };
  } catch {
    return createDefaultSave(normalized);
  }
}

function createHomePlayer(): PlayerSnapshot {
  return { position: [...HOME_POSITION], yaw: 0, pitch: HOME_PITCH };
}

export function hasWorldSave(seed: string, storage?: Storage): boolean {
  return safeGet(resolveStorage(storage), `${SAVE_PREFIX}${normalizeSeed(seed)}`) !== null;
}

export function saveWorld(save: WorldSave, storage?: Storage): void {
  const normalized = normalizeSeed(save.seed);
  const target = resolveStorage(storage);
  safeSet(target, `${SAVE_PREFIX}${normalized}`, JSON.stringify({ ...save, seed: normalized, updatedAt: Date.now() }));
  safeSet(target, ACTIVE_SEED_KEY, normalized);
}

export function resolveInitialSeed(storage?: Storage, search = window.location.search): string {
  return getUrlSeed(search) ?? normalizeSeed(safeGet(resolveStorage(storage), ACTIVE_SEED_KEY) ?? "misty-rice-fields");
}

export function shareSeedUrl(seed: string, location = window.location): string {
  const url = new URL(location.href);
  url.searchParams.set("seed", normalizeSeed(seed));
  return url.toString();
}

function sanitizePlayer(value: PlayerSnapshot): PlayerSnapshot {
  const position = value.position?.map((item) => Number(item)) as [number, number, number] | undefined;
  return {
    position: position?.length === 3 && position.every(Number.isFinite) ? position : [...HOME_POSITION],
    yaw: Number.isFinite(value.yaw) ? value.yaw : 0,
    pitch: Number.isFinite(value.pitch) ? value.pitch : 0,
  };
}

function sanitizeSettings(value: ExperienceSettings): ExperienceSettings {
  const weather = ["clear", "cloudy", "overcast", "mist", "drizzle", "after-rain"].includes(value.weather)
    ? value.weather
    : DEFAULT_SETTINGS.weather;
  const timePreset = ["dawn", "morning", "noon", "golden-hour", "night"].includes(value.timePreset)
    ? value.timePreset
    : DEFAULT_SETTINGS.timePreset;
  const quality = ["medium", "high"].includes(value.quality) ? value.quality : DEFAULT_SETTINGS.quality;
  return {
    weather,
    timePreset,
    quality,
    timeFlow: typeof value.timeFlow === "boolean" ? value.timeFlow : DEFAULT_SETTINGS.timeFlow,
    residents: typeof value.residents === "boolean" ? value.residents : DEFAULT_SETTINGS.residents,
    animals: typeof value.animals === "boolean" ? value.animals : DEFAULT_SETTINGS.animals,
    masterVolume: Math.min(1, Math.max(0, Number(value.masterVolume) || 0)),
    comfort: {
      fov: Math.min(90, Math.max(55, Number(value.comfort?.fov) || DEFAULT_SETTINGS.comfort.fov)),
      headBob: typeof value.comfort?.headBob === "boolean" ? value.comfort.headBob : DEFAULT_SETTINGS.comfort.headBob,
      motionBlur: typeof value.comfort?.motionBlur === "boolean" ? value.comfort.motionBlur : DEFAULT_SETTINGS.comfort.motionBlur,
      interactionHints: typeof value.comfort?.interactionHints === "boolean" ? value.comfort.interactionHints : DEFAULT_SETTINGS.comfort.interactionHints,
    },
  };
}

function resolveStorage(storage?: Storage): Storage | null {
  if (storage) return storage;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function safeGet(storage: Storage | null, key: string): string | null {
  if (!storage) return null;
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(storage: Storage | null, key: string, value: string): void {
  if (!storage) return;
  try {
    storage.setItem(key, value);
  } catch {
    // Persistence is optional; the walk remains usable when storage is blocked or full.
  }
}
